import { onBeforeUnmount, onMounted, shallowRef } from 'vue';
import type { LazyVideoPlayer } from './types';

// Preload запускаем, когда карточка ближе PRELOAD_MARGIN к вьюпорту, но не
// раньше события `load`, и дальше — в простое главного потока
// (requestIdleCallback), с фолбэком по таймеру.
const PRELOAD_MARGIN = '600px 0px';
const IDLE_TIMEOUT = 2000;
const FALLBACK_DELAY = 500;

/**
 * Лениво подгружает iframe rutube-плеера под постером (не по клику, а заранее —
 * при приближении к вьюпорту и в простое главного потока) и управляет
 * воспроизведением через Rutube Player API (postMessage), не грузя JS плеера со
 * страницей и не влияя на LCP/TBT первого экрана. См. README компонента.
 *
 * Порядок ожидания важен. Карточка первого экрана попадает во вьюпорт сразу,
 * поэтому один только requestIdleCallback не спасает: пока идёт гидрация,
 * простоя нет, и через IDLE_TIMEOUT колбэк выстреливает принудительно — ровно
 * посреди самой тяжёлой фазы. Ждём `load` (LCP и TBT к этому моменту уже
 * зафиксированы), и лишь потом ловим простой.
 */
export function useLazyVideoPlayer(getSrc: () => string): LazyVideoPlayer {
  const rootEl = shallowRef<HTMLElement | null>(null);
  const iframeEl = shallowRef<HTMLIFrameElement | null>(null);

  const isLoaded = shallowRef(false);
  const isReady = shallowRef(false);
  const isPlaying = shallowRef(false);
  const pendingPlay = shallowRef(false);

  let observer: IntersectionObserver | null = null;
  let onWindowLoad: (() => void) | null = null;

  function whenIdle() {
    const load = () => {
      isLoaded.value = true;
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(load, { timeout: IDLE_TIMEOUT });
    } else {
      setTimeout(load, FALLBACK_DELAY);
    }
  }

  function scheduleLoad() {
    if (document.readyState === 'complete') {
      whenIdle();
      return;
    }
    // `load` уже мог быть навешен предыдущим срабатыванием — не дублируем.
    if (onWindowLoad) return;
    onWindowLoad = () => {
      onWindowLoad = null;
      whenIdle();
    };
    window.addEventListener('load', onWindowLoad, { once: true });
  }

  function cancelWindowLoad() {
    if (!onWindowLoad) return;
    window.removeEventListener('load', onWindowLoad);
    onWindowLoad = null;
  }

  // Команды шлём строго на origin плеера, а не '*'.
  function resolveOrigin(): string {
    try {
      return new URL(getSrc(), window.location.href).origin;
    } catch {
      return '*';
    }
  }

  function sendPlayCommand() {
    const target = iframeEl.value?.contentWindow;
    if (!target) return;
    target.postMessage(
      JSON.stringify({ type: 'player:play', data: {} }),
      resolveOrigin(),
    );
    pendingPlay.value = false;
  }

  // Rutube шлёт postMessage {type:'player:ready'}, когда готов принимать команды
  // (https://rutube.ru/info/embed/#embed007).
  function handleMessage(event: MessageEvent) {
    if (!iframeEl.value || event.source !== iframeEl.value.contentWindow) return;
    if (typeof event.data !== 'string') return;

    let type: string | undefined;
    try {
      ({ type } = JSON.parse(event.data));
    } catch {
      return;
    }

    if (type === 'player:ready') {
      isReady.value = true;
      if (pendingPlay.value) sendPlayCommand();
    }
  }

  function play() {
    isPlaying.value = true;
    // Клик не ждёт ни `load`, ни простоя — вставляем iframe немедленно.
    isLoaded.value = true;
    cancelWindowLoad();

    // Если плеер ещё не прислал player:ready — откладываем команду до него.
    if (isReady.value) sendPlayCommand();
    else pendingPlay.value = true;
  }

  onMounted(() => {
    window.addEventListener('message', handleMessage);

    if (typeof IntersectionObserver === 'undefined' || !rootEl.value) {
      scheduleLoad();
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        scheduleLoad();
        observer?.disconnect();
        observer = null;
      },
      { rootMargin: PRELOAD_MARGIN },
    );
    observer.observe(rootEl.value);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('message', handleMessage);
    observer?.disconnect();
    cancelWindowLoad();
  });

  return { rootEl, iframeEl, isLoaded, isPlaying, play };
}
